// InterventionsTerrain — Visite Travaux · Recherche de fuite · Rapports de visite
//
// 🔑 UN SEUL composant pour les TROIS entrées de menu, paramétré par `type`.
//    ⛔ Pas trois écrans recopiés : la divergence entre copies est la cause-racine
//    de la majorité des régressions (CLAUDE.md, « une logique = une fonction »).
//
// CE QUI EST RÉUTILISÉ, ⛔ pas réécrit :
//   · `OHGrid` (components/ui/oh-grid) — tri par défaut created_at DESC, tri par
//     colonne, recherche, actions par ligne, sélection multiple. Les 4 conventions
//     de grille sont satisfaites par le socle, ⛔ pas réimplémentées ici.
//   · `GET /api/metier/dossiers` et `GET /api/metier/clients` — endpoints
//     existants, déjà scopés au tenant par le middleware (le slug fait autorité,
//     DEC-084). ⛔ Aucun endpoint nouveau.
//   · `formatDateFR` (utils/dateFormat) — formatage unique de l'application.
//
// ⛔ CE QUE CET ÉCRAN N'EST PAS, et le dit à l'utilisateur : le Visit Engine.
//    Pas de dictée, pas de photos, pas de rapport d'assurance. L'état affiché est
//    HONNÊTE : ce qui existe est listé, ce qui viendra est nommé.
import { useState, useEffect, useCallback, useMemo } from "react";
import { Hammer, Droplets, FileText, ExternalLink } from "lucide-react";
import { OHGrid } from "../../components/ui/oh-grid";
import { formatDateFR } from "../../utils/dateFormat";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

// Le discriminant porté par le dossier. Un dossier sans ce champ n'est pas une
// intervention terrain — il n'apparaît donc dans aucune des trois grilles.
const CHAMP_TYPE = "type_intervention";

export const TYPES_INTERVENTION = {
  visite_travaux: {
    titre: "Visite Travaux",
    icone: Hammer,
    sousTitre: "Visites de chantier : constat, métré, éléments pour le devis.",
    aVenir: "Le relevé guidé sur mobile (dictée, photos, métré assisté) n'est pas encore ouvert.",
  },
  recherche_fuite: {
    titre: "Recherche de fuite",
    icone: Droplets,
    sousTitre: "Interventions de recherche de fuite et de diagnostic.",
    aVenir: "Le rapport de recherche destiné à l'assurance n'est pas encore ouvert.",
  },
  rapports_visite: {
    titre: "Rapports de visite",
    icone: FileText,
    sousTitre: "Comptes rendus produits à l'issue des visites.",
    aVenir: "La génération automatique du compte rendu n'est pas encore ouverte.",
  },
};

export default function InterventionsTerrain({ type = "visite_travaux", onOpenDossier }) {
  const conf = TYPES_INTERVENTION[type] || TYPES_INTERVENTION.visite_travaux;
  const Icone = conf.icone;
  const [lignes, setLignes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const [rd, rc] = await Promise.all([
        fetch(`${API}/api/metier/dossiers`, { headers: auth(), credentials: "include" }),
        fetch(`${API}/api/metier/clients`, { headers: auth(), credentials: "include" }),
      ]);
      if (!rd.ok) throw new Error(`dossiers HTTP ${rd.status}`);
      const dossiers = await rd.json();
      const clients = rc.ok ? await rc.json() : [];
      const parId = new Map(
        (Array.isArray(clients) ? clients : []).map((c) => [
          c.id || c._id,
          [c.nom, c.prenom].filter(Boolean).join(" ") || c.reference || "—",
        ])
      );
      const liste = (Array.isArray(dossiers) ? dossiers : [])
        // ⚠️ Le filtre porte sur le dossier lui-même : on ne fabrique pas une
        // collection parallèle. Un dossier devient une intervention en portant
        // ce champ — c'est le seul point de couplage.
        .filter((d) => (d?.[CHAMP_TYPE] || "") === type)
        .map((d) => ({
          ...d,
          id: d.id || d._id,
          client_nom: parId.get(d.client_id) || "—",
          cree_le: formatDateFR(d.created_at),
        }));
      setLignes(liste);
    } catch (e) {
      setErreur(e.message || "Chargement impossible");
      setLignes([]);
    } finally {
      setChargement(false);
    }
  }, [type]);

  useEffect(() => { charger(); }, [charger]);

  const colonnes = useMemo(() => [
    { key: "reference", label: "Référence", mono: true, sortable: true },
    { key: "client_nom", label: "Client", sortable: true },
    { key: "statut", label: "Statut", badge: true, sortable: true },
    { key: "cree_le", label: "Créée le", muted: true, sortable: true },
  ], []);

  const actions = useMemo(() => ([
    {
      label: "Ouvrir le dossier",
      icon: ExternalLink,
      onClick: (row) => onOpenDossier?.(row.id),
    },
  ]), [onOpenDossier]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start gap-3">
        <div
          className="p-2 rounded-lg"
          style={{ background: "var(--oh-accent-bg)", color: "var(--oh-accent-text)" }}
        >
          <Icone className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl" style={{ color: "var(--oh-text)", fontWeight: 500 }}>
            {conf.titre}
          </h1>
          <p className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>
            {conf.sousTitre}
          </p>
        </div>
      </div>

      {/* ⚠️ L'état est dit AVANT la grille, ⛔ pas caché dans un coin : l'utilisateur
          doit comprendre ce qu'il voit ET ce qui viendra. */}
      <div
        className="rounded-lg p-3 text-sm"
        style={{
          background: "var(--oh-info-bg)",
          color: "var(--oh-info-text)",
          border: "0.5px solid var(--oh-info-border)",
        }}
        data-testid={`interventions-etat-${type}`}
      >
        Cette vue liste les dossiers de votre organisation marqués «&nbsp;{conf.titre}&nbsp;».
        Une intervention se crée depuis un dossier. {conf.aVenir}
      </div>

      {erreur && (
        <div
          className="rounded-lg p-3 text-sm"
          style={{
            background: "var(--oh-error-bg)",
            color: "var(--oh-error-text)",
            border: "0.5px solid var(--oh-error-border)",
          }}
        >
          {erreur}
        </div>
      )}

      <OHGrid
        data={lignes}
        columns={colonnes}
        rowActions={actions}
        onRowClick={(row) => onOpenDossier?.(row.id)}
        loading={chargement}
        onRefresh={charger}
        selectable
        defaultSortKey="created_at"
        defaultSortDir="desc"
        searchPlaceholder={`Rechercher une ${conf.titre.toLowerCase()}…`}
        emptyMessage={`Aucune ${conf.titre.toLowerCase()} enregistrée pour le moment. Marquez un dossier comme « ${conf.titre} » pour le voir apparaître ici.`}
        testIdPrefix={`interventions-${type}`}
      />
    </div>
  );
}
